import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import api from "../../services/api";

interface CustomerComboboxProps {
  customerId?: string;
  customerName?: string;
  onChange: (customerId: string | undefined, customerName: string | undefined) => void;
}

interface PartnerOption {
  id: string;
  label: string;
}

/**
 * Hybrid customer field (mirrors the Tasks client field): type a free-text
 * company name OR pick from the partners directory. Selecting stores both
 * customerId + customerName; typing free text stores only customerName.
 */
export const CustomerCombobox: React.FC<CustomerComboboxProps> = ({
  customerId,
  customerName,
  onChange,
}) => {
  const { t } = useTranslation("works");
  const [inputValue, setInputValue] = useState(customerName ?? "");
  const [suggestions, setSuggestions] = useState<PartnerOption[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(customerName ?? "");
  }, [customerName]);

  const fetchSuggestions = useCallback(async (search: string) => {
    if (!search.trim()) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await api.get("/partners", {
        params: { search: search.trim(), page: 1, limit: 8 },
      });
      const partners: { _id: string; companyName: string }[] =
        res.data.data?.partners ?? [];
      setSuggestions(partners.map((p) => ({ id: p._id, label: p.companyName })));
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    // Free text: drop the linked partner id, keep the typed name.
    onChange(undefined, val || undefined);
    setOpen(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelect = (opt: PartnerOption) => {
    setInputValue(opt.label);
    onChange(opt.id, opt.label);
    setSuggestions([]);
    setOpen(false);
  };

  const handleClear = () => {
    setInputValue("");
    onChange(undefined, undefined);
    setSuggestions([]);
    setOpen(false);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative flex items-center h-[34px] sm:h-[38px] rounded-lg border bg-white overflow-hidden border-[rgba(11,15,14,0.15)] focus-within:ring-2 focus-within:ring-[#C97A3C] focus-within:border-transparent">
        <span className="flex items-center justify-center min-w-[28px] sm:min-w-[38px] px-1.5 sm:px-2 h-full bg-[#F5F4F0] border-r border-[#ECEAE4] text-[#8A9590] shrink-0">
          <Search size={13} />
        </span>
        <input
          type="text"
          className="flex-1 min-w-0 h-full border-0 bg-transparent px-3 text-[13px] text-[#0B0F0E] focus:outline-none focus:ring-0"
          placeholder={t("form.placeholders.customer")}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => {
            if (inputValue) fetchSuggestions(inputValue);
            setOpen(true);
          }}
        />
        {customerId && (
          <span className="text-[10px] text-[#3B6FB6] bg-[#EAF1FB] border border-[#C5D9F2] rounded px-1 mr-1 shrink-0 whitespace-nowrap">
            {t("form.partnerBadge")}
          </span>
        )}
        {inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="p-1 text-[#8A9590] hover:text-[#E24B4A] mr-0.5 shrink-0"
            aria-label={t("actions.clear")}
          >
            <X size={13} />
          </button>
        )}
      </div>

      {open && (inputValue || suggestions.length > 0) && (
        <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#ECEAE4] rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {loading && (
            <div className="px-3 py-2 text-[12px] text-[#8A9590]">{t("form.searching")}</div>
          )}
          {!loading && suggestions.length === 0 && inputValue.trim() && (
            <div className="px-3 py-2 text-[12px] text-[#8A9590]">
              {t("form.customerFreeText", { name: inputValue })}
            </div>
          )}
          {suggestions.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className="w-full text-left px-3 py-2 text-[13px] text-[#2B2F2E] hover:bg-[#FAF9F6] transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(opt);
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
